/**
 * The one audio object the UI talks to. Owns the AudioContext, a single
 * master gain, both engines, the sweep loop and the sweep-highlight poller.
 * React describes what should be sounding as a ``SonificationScene`` and calls
 * ``apply``; everything stateful about audio lives here, not in effects.
 */

import type { LineSolo } from "../domain/lines";
import { AudioEngine, type AudioParameters, type LayerGains, type StackedItem } from "./engine";
import { FeatureEngine, type FeatureLayerGains } from "./featureEngine";
import type { FeatureAnalysis } from "./features";
import { MASTER_OUTPUT_BOOST, TIME_CONSTANT_S, createUnlockedAudioContext } from "./graph";

export type ClassicScene =
  | { kind: "single"; params: AudioParameters }
  | { kind: "stacked"; items: StackedItem[]; layerGains: LayerGains };

export interface FeatureScene {
  analysis: FeatureAnalysis;
  /** Bright-state Hβ flux, the loudness denominator for every feature. */
  referenceFlux: number;
  continuumScale: number;
  layerGains: FeatureLayerGains;
  /** Epoch whose arrival should trigger an automatic sweep; null to disable. */
  sweepEpoch: number | null;
  sweepLoop: boolean;
  sweepRate: number;
  /** Solo applied to the sweep plucks (the sweep solo itself plays everything). */
  sweepSolo: LineSolo;
}

export interface SonificationScene {
  enabled: boolean;
  masterGain: number;
  solo: LineSolo;
  classic: ClassicScene | null;
  /** Non-null while the feature engine is the audible one. */
  feature: FeatureScene | null;
}

export class Sonifier {
  private readonly ctx: AudioContext;
  private readonly master: GainNode;
  private readonly classic: AudioEngine;
  private readonly feature: FeatureEngine;
  private scene: SonificationScene | null = null;
  private lastSweepEpoch: number | null = null;
  private highlightRaf: number | null = null;
  private highlightLambda: number | null = null;
  private readonly listeners = new Set<(lambdaA: number | null) => void>();

  private constructor(ctx: AudioContext, classic: AudioEngine, feature: FeatureEngine, master: GainNode) {
    this.ctx = ctx;
    this.master = master;
    this.classic = classic;
    this.feature = feature;
    document.addEventListener("visibilitychange", this.revive);
    document.addEventListener("pointerdown", this.revive);
  }

  /** Must be called from a user gesture (see ``createUnlockedAudioContext``). */
  static async create(): Promise<Sonifier> {
    const ctx = await createUnlockedAudioContext();
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    const classic = await AudioEngine.create(ctx, master);
    const feature = new FeatureEngine(ctx, master);
    return new Sonifier(ctx, classic, feature, master);
  }

  /** Called within a tap so iOS lets a suspended or interrupted context restart. */
  resume(): void {
    if (this.ctx.state !== "running") void this.ctx.resume();
  }

  /**
   * iOS suspends or "interrupts" the context on app switch, screen lock or a
   * call. Retry on return to the page and on the next tap (which carries the
   * gesture iOS may insist on).
   */
  private readonly revive = (): void => {
    if (this.scene?.enabled && document.visibilityState === "visible") this.resume();
  };

  /** Bring the audio graph in line with ``scene``. Idempotent. */
  apply(scene: SonificationScene): void {
    const prev = this.scene;
    this.scene = scene;
    const now = this.ctx.currentTime;
    const featureActive = scene.enabled && scene.feature !== null;

    this.master.gain.setTargetAtTime(
      scene.enabled ? scene.masterGain * MASTER_OUTPUT_BOOST : 0,
      now,
      TIME_CONSTANT_S,
    );
    this.classic.setActive(scene.enabled && scene.feature === null);
    this.feature.setActive(featureActive);

    if (scene.classic && (scene.classic !== prev?.classic || scene.solo !== prev?.solo)) {
      if (scene.classic.kind === "single") this.classic.update(scene.classic.params);
      else this.classic.updateStacked(scene.classic.items, scene.classic.layerGains);
    }

    const f = scene.feature;
    if (!f) {
      this.lastSweepEpoch = null;
      this.stopHighlight();
      return;
    }
    this.feature.setLayerGains(f.layerGains);
    this.feature.setSweepRate(f.sweepRate);
    if (f.analysis !== prev?.feature?.analysis || scene.solo !== prev?.solo || f.referenceFlux !== prev?.feature?.referenceFlux || f.continuumScale !== prev?.feature?.continuumScale) {
      this.feature.update(f.analysis.features, f.analysis.summary, f.referenceFlux, f.continuumScale, scene.solo);
    }

    // Auto-sweep announces each new epoch; arming on the first one stays silent.
    if (!scene.enabled || f.sweepEpoch === null) {
      this.lastSweepEpoch = null;
    } else if (this.lastSweepEpoch !== f.sweepEpoch) {
      const firstArm = this.lastSweepEpoch === null;
      this.lastSweepEpoch = f.sweepEpoch;
      if (!firstArm) this.playSweep();
    }

    if (f.sweepLoop && scene.enabled && this.highlightRaf === null) this.playSweep();
  }

  /** Play the earcon sweep for the current feature set and track its highlight. */
  playSweep(): void {
    const f = this.scene?.feature;
    if (!f) return;
    const started = this.feature.playSweep(f.analysis.features, f.referenceFlux, f.sweepSolo);
    this.stopHighlight();
    if (started) this.highlightRaf = requestAnimationFrame(this.tickHighlight);
  }

  /** Subscribe to the wavelength of the pluck sounding now (null when none). */
  onSweepLambda(listener: (lambdaA: number | null) => void): () => void {
    this.listeners.add(listener);
    listener(this.highlightLambda);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private readonly tickHighlight = (): void => {
    const progress = this.feature.sweepProgress();
    if (!progress || progress.finished) {
      this.highlightRaf = null;
      if (progress && this.scene?.feature?.sweepLoop) {
        // Relaunch against the current scene, not the one the sweep started with.
        this.playSweep();
      } else {
        this.emit(null);
      }
      return;
    }
    this.emit(progress.lambdaA);
    this.highlightRaf = requestAnimationFrame(this.tickHighlight);
  };

  private stopHighlight(): void {
    if (this.highlightRaf !== null) cancelAnimationFrame(this.highlightRaf);
    this.highlightRaf = null;
    this.emit(null);
  }

  private emit(lambdaA: number | null): void {
    if (lambdaA === this.highlightLambda) return;
    this.highlightLambda = lambdaA;
    for (const listener of this.listeners) listener(lambdaA);
  }

  dispose(): void {
    document.removeEventListener("visibilitychange", this.revive);
    document.removeEventListener("pointerdown", this.revive);
    this.stopHighlight();
    this.feature.dispose();
    this.master.disconnect();
  }
}
